import * as React from "react";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { PlainDateCalendar } from "./plaindateCalendar";

function DatePicker_() {
	const [date, setDate] = React.useState<Temporal.PlainDate | undefined>();

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant={"secondary"}
					className={cn(
						"justify-start text-left",
						!date && "text-muted-foreground",
					)}
				>
					<CalendarIcon />
					{date ? date.toLocaleString() : <span>Elige una fecha</span>}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<PlainDateCalendar
					mode="single"
					value={date}
					setValue={setDate}
					autoFocus
				/>
			</PopoverContent>
		</Popover>
	);
}

function DateRangePicker_(props: {
	value: {
		from: Temporal.PlainDate | undefined;
		to: Temporal.PlainDate | undefined;
	};
	onChange: (range: {
		from: Temporal.PlainDate | undefined;
		to: Temporal.PlainDate | undefined;
	}) => void;
	literal?: string;
}) {
	const { value, onChange, ...otherProps } = props;

	const date = [value?.from, value?.to] as const;
	const setDate = (
		range: [Temporal.PlainDate | undefined, Temporal.PlainDate | undefined],
	) => {
		onChange?.({
			from: range[0],
			to: range[1],
		});
	};

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					id="date"
					variant="outline"
					className={cn(
						"w-[300px] justify-start text-left font-normal",
						!date && "text-muted-foreground",
					)}
					{...otherProps}
				>
					<CalendarIcon className="mr-2 h-4 w-4" />
					{date?.[0] ? (
						date?.[1] ? (
							<>
								{format(date?.[0])} - {format(date?.[1])}
							</>
						) : (
							format(date?.[0])
						)
					) : (
						<span>{otherProps.literal ?? "Elige una fecha"}</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<PlainDateCalendar
					mode="range"
					value={date}
					setValue={setDate}
					numberOfMonths={2}
				/>
			</PopoverContent>
		</Popover>
	);
}

export const DatePicker = React.memo(DatePicker_);
export const DateRangePicker = React.memo(DateRangePicker_);

function format(
	date: Temporal.PlainDate,
	locale?: string,
	format?: Intl.DateTimeFormatOptions,
) {
	return new Intl.DateTimeFormat(locale, format).format(date);
}
